import type { WorkspaceResponseV2 } from '@emma/api_client/types';
import {
  Button,
  ButtonGroup,
  FormControl,
  Icon,
  Modal,
  Text,
  Tooltip,
  useToast,
} from '@lego/klik-ui';
import { PlusBold } from '@lego/klik-ui/icons';
import { useCallback, useMemo, useState } from 'react';
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import {
  incomingDeviceTransferTableColumns,
  outgoingDeviceTransferTableColumns,
} from '@/components/common/table/column_definitions/DeviceTransfer';
import { TableStoreProvider } from '@/components/common/table/store/Context';
import { Table } from '@/components/common/table/Table';
import { useApiFetch } from '@/hooks/api';
import {
  type DeviceTransferAction,
  useCreateDeviceTransfer,
  useDeviceTransfers,
  useUpdateDeviceTransfer,
} from '@/hooks/device_transfer';
import { useCanOperateById, useWorkspaces } from '@/hooks/workspace';
import { ToastOptions } from '@/utility/ToastOptions';

type Props = {
  workspace: WorkspaceResponseV2;
};

const FETCH_LIMIT = 1000;

const ACTION_LABELS: Record<DeviceTransferAction, string> = {
  APPROVE: 'approve',
  DENY: 'deny',
  RETRACT: 'retract',
};

const PAST_TENSE_LABELS: Record<DeviceTransferAction, string> = {
  APPROVE: 'approved',
  DENY: 'denied',
  RETRACT: 'retracted',
};

const IncomingDeviceTransferTableContent = (props: Props) => {
  const toast = useToast();
  const canOperate = useCanOperateById(props.workspace.id);
  const updateDeviceTransfer = useUpdateDeviceTransfer();

  const [pendingAction, setPendingAction] = useState<{
    transferId: number;
    action: DeviceTransferAction;
  } | null>(null);

  const { data, isLoading, isValidating, mutate } = useDeviceTransfers({
    toWorkspaceIdIn: [props.workspace.id],
    limit: FETCH_LIMIT,
  });

  const handleAction = useCallback((transferId: number, action: DeviceTransferAction) => {
    setPendingAction({ transferId, action });
  }, []);

  const onConfirmed = async () => {
    if (!pendingAction) return;
    const { transferId, action } = pendingAction;
    setPendingAction(null);
    const result = await updateDeviceTransfer({ deviceTransferId: transferId, status: action });
    if (result) {
      toast(
        ToastOptions.success(
          `Transfer ${PAST_TENSE_LABELS[action]}`,
          `Device transfer #${transferId} has been ${PAST_TENSE_LABELS[action]}.`,
          3000
        )
      );
      void mutate();
    }
  };

  const columns = useMemo(
    () => incomingDeviceTransferTableColumns(canOperate ? handleAction : undefined),
    [canOperate, handleAction]
  );
  const transfers = data?.deviceTransfers || [];

  return (
    <>
      <ConfirmationDialog
        body={`Are you sure you want to ${pendingAction ? ACTION_LABELS[pendingAction.action] : ''} this device transfer?`}
        header={`${pendingAction ? pendingAction.action.charAt(0) + pendingAction.action.slice(1).toLowerCase() : ''} Device Transfer`}
        isOpen={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        onConfirmed={onConfirmed}
      />
      <Table
        columns={columns}
        count={transfers.length}
        data={transfers}
        isLoading={isLoading}
        isValidating={isValidating}
        refresh={mutate}
        title="Incoming Device Transfers"
      />
    </>
  );
};

export const IncomingDeviceTransferTable = (props: Props) => (
  <TableStoreProvider initialSorting={[{ desc: true, id: 'id' }]} name="IncomingDeviceTransfers">
    <IncomingDeviceTransferTableContent {...props} />
  </TableStoreProvider>
);

const OutgoingDeviceTransferTableContent = (props: Props) => {
  const toast = useToast();
  const canOperate = useCanOperateById(props.workspace.id);
  const createDeviceTransfer = useCreateDeviceTransfer();
  const updateDeviceTransfer = useUpdateDeviceTransfer();

  const [pendingAction, setPendingAction] = useState<{
    transferId: number;
    action: DeviceTransferAction;
  } | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [targetWorkspaceId, setTargetWorkspaceId] = useState<number | undefined>(undefined);
  const [deviceId, setDeviceId] = useState<string>('');

  const { data, isLoading, isValidating, mutate } = useDeviceTransfers({
    fromWorkspaceIdIn: [props.workspace.id],
    limit: FETCH_LIMIT,
  });

  const { data: workspacesData } = useWorkspaces(showCreateModal ? {} : null);

  const { data: devicesData } = useApiFetch(
    showCreateModal ? `devices?workspaceIdIn=${props.workspace.id}` : null,
    async (callApi) =>
      await callApi((api) =>
        api.devices.getV5Devices({
          workspaceIdIn: [props.workspace.id],
          stateIn: ['PROVISIONED'],
          limit: FETCH_LIMIT,
        })
      )
  );

  const workspaceOptions = useMemo(
    () =>
      (workspacesData?.workspaces ?? [])
        .filter((ws) => ws.id !== props.workspace.id)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((ws) => ({ label: `${ws.name} (#${ws.id})`, value: String(ws.id) })),
    [workspacesData, props.workspace.id]
  );

  const deviceOptions = useMemo(
    () =>
      (devicesData?.data.devices ?? [])
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((d) => ({ label: `${d.name} (#${d.id})`, value: String(d.id) })),
    [devicesData]
  );

  const handleAction = useCallback((transferId: number, action: DeviceTransferAction) => {
    setPendingAction({ transferId, action });
  }, []);

  const onConfirmed = async () => {
    if (!pendingAction) return;
    const { transferId, action } = pendingAction;
    setPendingAction(null);
    const result = await updateDeviceTransfer({ deviceTransferId: transferId, status: action });
    if (result) {
      toast(
        ToastOptions.success(
          `Transfer ${PAST_TENSE_LABELS[action]}`,
          `Device transfer #${transferId} has been ${PAST_TENSE_LABELS[action]}.`,
          3000
        )
      );
      void mutate();
    }
  };

  const onCreateTransfer = async () => {
    const parsedDeviceId = Number.parseInt(deviceId, 10);
    if (!targetWorkspaceId || !parsedDeviceId) return;
    setShowCreateModal(false);
    const result = await createDeviceTransfer({
      fromWorkspaceId: props.workspace.id,
      toWorkspaceId: targetWorkspaceId,
      deviceId: parsedDeviceId,
    });
    if (result) {
      toast(
        ToastOptions.success(
          'Transfer requested',
          `Device transfer for device #${parsedDeviceId} has been created.`,
          3000
        )
      );
      void mutate();
    }
    setTargetWorkspaceId(undefined);
    setDeviceId('');
  };

  const columns = useMemo(
    () => outgoingDeviceTransferTableColumns(canOperate ? handleAction : undefined),
    [canOperate, handleAction]
  );
  const transfers = data?.deviceTransfers || [];

  return (
    <>
      <ConfirmationDialog
        body={`Are you sure you want to ${pendingAction ? ACTION_LABELS[pendingAction.action] : ''} this device transfer?`}
        header={`${pendingAction ? pendingAction.action.charAt(0) + pendingAction.action.slice(1).toLowerCase() : ''} Device Transfer`}
        isOpen={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        onConfirmed={onConfirmed}
      />
      <Modal
        hideIcon={true}
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setTargetWorkspaceId(undefined);
          setDeviceId('');
        }}
        size="xl"
        variant="warning"
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header fontSize="xl">Create Device Transfer</Modal.Header>
          <Modal.CloseButton />
          <Modal.Body>
            <Text fontSize="md" mb={4}>
              Transfer a device from workspace #{props.workspace.id} to another workspace. The
              target workspace owner will need to approve the transfer.
            </Text>
            <FormControl isRequired mb={4}>
              <FormControl.Label size="sm">Device</FormControl.Label>
              <SearchableSelect
                onChange={(val) => setDeviceId(val)}
                options={deviceOptions}
                placeholder="Search devices..."
                value={deviceId}
              />
            </FormControl>
            <FormControl isRequired>
              <FormControl.Label size="sm">Target Workspace</FormControl.Label>
              <SearchableSelect
                onChange={(val) => setTargetWorkspaceId(Number.parseInt(val, 10))}
                options={workspaceOptions}
                placeholder="Search workspaces..."
                value={targetWorkspaceId ? String(targetWorkspaceId) : undefined}
              />
            </FormControl>
          </Modal.Body>
          <Modal.Footer>
            <ButtonGroup>
              <Button
                colorScheme="warning"
                isDisabled={!targetWorkspaceId || !Number.parseInt(deviceId, 10)}
                onClick={onCreateTransfer}
              >
                Confirm Transfer
              </Button>
              <Button
                onClick={() => {
                  setShowCreateModal(false);
                  setTargetWorkspaceId(undefined);
                  setDeviceId('');
                }}
                variant="outline"
              >
                Cancel
              </Button>
            </ButtonGroup>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
      <Table
        addButton={
          canOperate
            ? () => (
                <Tooltip label="Create a new device transfer" placement="left">
                  <Button
                    borderRightRadius="0px"
                    leftIcon={<Icon as={PlusBold} />}
                    onClick={() => setShowCreateModal(true)}
                    size="sm"
                    variant="outline"
                  >
                    Add
                  </Button>
                </Tooltip>
              )
            : undefined
        }
        columns={columns}
        count={transfers.length}
        data={transfers}
        isLoading={isLoading}
        isValidating={isValidating}
        refresh={mutate}
        title="Outgoing Device Transfers"
      />
    </>
  );
};

export const OutgoingDeviceTransferTable = (props: Props) => (
  <TableStoreProvider initialSorting={[{ desc: true, id: 'id' }]} name="OutgoingDeviceTransfers">
    <OutgoingDeviceTransferTableContent {...props} />
  </TableStoreProvider>
);
